import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { up } from 'styled-breakpoints';
import {
  Mutation,
  MutationChangeTaskStatusArgs,
  Task,
  TaskStatus,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { OptionsBottomDrawer } from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { SelectableOptionsDrawer } from 'src/components/general/drawer/bottom/selectable-options-bottom-drawer';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { useOpenCallContactModal } from 'src/components/pages/conversations/components/open-legacy-call-modal-icon';
import { CHANGE_TASK_STATUS_MUTATION } from 'src/components/pages/tasks/queries';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useShowInlineFormModal } from 'src/hooks/use-show-inline-form-modal';
import { useUrls } from 'src/hooks/use-urls';
import { VisibilityHandler } from 'src/hooks/use-visible';

type TaskOptionsBottomDrawerProps = {
  onClose: VoidFunction;
  selectedTask: Task | null;
  visible: boolean;
  mobileStatusOptionsVisibilityHandler: VisibilityHandler;
};

export const TaskOptionsBottomDrawer = memo<TaskOptionsBottomDrawerProps>(
  ({ onClose, selectedTask, visible, mobileStatusOptionsVisibilityHandler }) => {
    const { t } = useTranslation(['tasks']);
    const { getContactModalUrl, getTaskModalUrl, getTaskInlineFormUrl } = useUrls();
    const showInlineFormModal = useShowInlineFormModal();
    const breakpointUpMd = useBreakpoint(up('md'));
    const openCallContactModal = useOpenCallContactModal();
    const history = useHistory();
    const options = useMemo(() => {
      const options = [
        {
          onClick: () => {
            if (selectedTask) {
              history.push(
                showInlineFormModal
                  ? getTaskInlineFormUrl(selectedTask.id)
                  : getTaskModalUrl({ taskId: selectedTask.id }),
              );
              onClose();
            }
          },
          label: t('tasks:taskOptions.viewTask'),
          icon: 'task18',
          'data-lgg-id': 'task-option-view-task',
        },
        {
          onClick: () => {
            mobileStatusOptionsVisibilityHandler.setVisible(true);
            onClose();
          },
          label: t('tasks:taskOptions.updateStatus'),
          icon: 'updateStatus',
          'data-lgg-id': 'task-option-update-status',
        },
      ];

      if (selectedTask?.contact) {
        options.push({
          onClick: () => {
            if (selectedTask.contact) {
              history.push(getContactModalUrl({ contactId: selectedTask?.contact?.id }));
              onClose();
            }
          },
          label: t('tasks:taskOptions.viewContact'),
          icon: 'contact',
          'data-lgg-id': 'task-option-view-contact',
        });

        options.push({
          onClick: () => {
            if (selectedTask.contact) {
              void openCallContactModal(selectedTask.contact.id);
              onClose();
            }
          },
          label: t('tasks:taskOptions.callContact'),
          icon: 'call',
          'data-lgg-id': 'task-option-call-contact',
        });
      }

      return options;
    }, [
      t,
      selectedTask,
      history,
      getTaskModalUrl,
      getTaskInlineFormUrl,
      showInlineFormModal,
      onClose,
      mobileStatusOptionsVisibilityHandler,
      getContactModalUrl,
      openCallContactModal,
    ]);

    return (
      <OptionsBottomDrawer
        onClose={onClose}
        title={t('tasks:taskOptions.title')}
        visible={
          !breakpointUpMd && visible && !mobileStatusOptionsVisibilityHandler.visible
        }
        options={options}
      />
    );
  },
);

type ChangeStatusDrawerProps = {
  visibilityHandler: VisibilityHandler;
  task: Pick<Task, 'id' | 'status'>;
  onCompleted: VoidFunction;
};

// Reuse in task page
export const ChangeTaskStatusDrawer = memo(
  ({ visibilityHandler, task, onCompleted }: ChangeStatusDrawerProps) => {
    const { t } = useTranslation(['common', 'tasks']);
    const [changeTaskStatus] = useMutation<
      Pick<Mutation, 'changeTaskStatus'>,
      MutationChangeTaskStatusArgs
    >(CHANGE_TASK_STATUS_MUTATION);

    const showNotification = useShowNotification();

    const taskStatusBaseOptions = [
      {
        value: 'YET_TO_START',
        label: t('tasks:status.yetToStart'),
        'data-lgg-id': 'task-status-option-yet-to-start',
        icon: 'taskYetToStart',
      },

      {
        value: 'IN_PROGRESS',
        label: t('tasks:status.inProgress'),
        'data-lgg-id': 'task-status-option-in-progress',
        icon: 'taskInProgress',
      },
      {
        value: 'COMPLETED',
        label: t('tasks:status.completed'),
        'data-lgg-id': 'task-status-option-completed',
        icon: 'taskCompleted',
      },
    ];

    const getTaskStatusOptions = (taskId: number) => {
      return taskStatusBaseOptions.map((statusOption) => ({
        ...statusOption,
        value: statusOption.value,
        onClick: async () => {
          await changeTaskStatus({
            variables: {
              input: {
                taskId,
                status: statusOption.value as TaskStatus,
              },
            },
            onCompleted: async () => {
              visibilityHandler.close();

              await onCompleted();

              showNotification({
                type: 'success',
                title: t('tasks:tasksUpdatedMessages.success'),
              });
            },
            onError: () => {
              showNotification({
                type: 'error',
                title: t('tasks:tasksUpdatedMessages.error'),
              });
            },
          });
        },
      }));
    };
    const taskStatusOptions = getTaskStatusOptions(Number(task.id));

    return (
      <SelectableOptionsDrawer
        visible={visibilityHandler.visible}
        title={t('tasks:status.title')}
        onClose={visibilityHandler.close}
        options={taskStatusOptions}
        selectedValue={task.status}
      />
    );
  },
);
